// --- START OF FILE src/components/Attendance/MonthlyReportSection.js (FINAL CORRECTED VERSION) ---

import React, { useState, useEffect, useRef } from 'react';
import { useFirebase } from '../../App';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';

// --- NEW NPM IMPORTS ---
import jsPDF from 'jspdf'; // Make sure jspdf is installed: npm install jspdf
import html2canvas from 'html2canvas'; // Make sure html2canvas is installed: npm install html2canvas

import * as Sentry from "@sentry/react";

function MonthlyReportSection() {
    const { db } = useFirebase();
    const [employees, setEmployees] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const reportRef = useRef(null);

    useEffect(() => {
        if (!db) { setEmployees([]); return; }
        const workersCol = collection(db, `workers`);
        const q = query(workersCol, orderBy("fullName"));
        getDocs(q).then(snapshot => {
            setEmployees(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        }).catch(err => {
            toast.error("Could not fetch employee list for reports.");
            Sentry.captureException(err);
        });
    }, [db]);
    
    const toYYYYMMDD = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    const handleGenerateReport = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setReportData(null);

        const form = e.target;
        const employeeId = form.elements['employee-select'].value;
        const month = parseInt(form.elements['month-select'].value);
        const year = parseInt(form.elements['year-select'].value);

        if (!employeeId) { toast.error("Please select an employee."); setIsLoading(false); return; }
        const employee = employees.find(emp => emp.id === employeeId);

        try {
            const startDateOfMonth = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0);
            const startDateStr = toYYYYMMDD(startDateOfMonth);
            const endDateStr = toYYYYMMDD(endDate);
            
            const q = query(collection(db, `attendance`), where("employeeId", "==", employeeId), where("date", ">=", startDateStr), where("date", "<=", endDateStr));
            const snapshot = await getDocs(q);
            const attendanceLogs = snapshot.docs.map(doc => doc.data());

            const daysInMonth = endDate.getDate();
            const today = new Date(); today.setHours(0, 0, 0, 0);
            
            const fullMonthData = Array.from({ length: daysInMonth }, (_, i) => {
                const currentDate = new Date(year, month, i + 1);
                const dateStr = toYYYYMMDD(currentDate);
                const logForDay = attendanceLogs.find(log => log.date === dateStr);

                const entry = {
                    date: currentDate.toLocaleDateString('en-GB'),
                    checkIn: logForDay?.checkIn ? logForDay.checkIn.toDate().toLocaleTimeString() : '--',
                    checkOut: logForDay?.checkOut ? logForDay.checkOut.toDate().toLocaleTimeString() : '--',
                    location: logForDay?.checkInLocation || { name: 'N/A' },
                    status: 'N/A',
                };

                if (logForDay) {
                    entry.status = logForDay.status;
                } else if (currentDate.getDay() === 0) { // Check if it's Sunday
                    entry.status = 'Holiday';
                } else if (currentDate <= today) { // Mark as absent if past date and no log
                    entry.status = 'Absent';
                } else { // Future dates
                    entry.status = 'Scheduled';
                }
                return entry;
            });

            const summary = fullMonthData.reduce((acc, day) => {
                if (['Present', 'Late', 'Completed'].includes(day.status)) acc.present++;
                if (day.status === 'Late') acc.late++;
                if (day.status === 'Absent') acc.absent++;
                if (day.status === 'Holiday') acc.holiday++;
                return acc;
            }, { present: 0, absent: 0, late: 0, holiday: 0 });

            setReportData({ employee, period: `${new Date(year, month).toLocaleString('default', { month: 'long' })} ${year}`, logs: fullMonthData, summary });
        } catch (error) { 
            toast.error("Failed to generate report."); 
            console.error(error); // <--- Add this line
            Sentry.captureException(error); 
        } 
        finally { setIsLoading(false); }
    };

    const handleExport = () => {
        // html2canvas and jspdf are now imported, so no need for window. check
        const input = reportRef.current;
        if (!input) { toast.error("Report content not found."); return; }
        
        toast.info("Generating PDF...");
        const buttonToHide = input.querySelector('.export-pdf-button');
        if (buttonToHide) buttonToHide.style.display = 'none';

        html2canvas(input, { scale: 2 }).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Attendance_Report_${reportData.employee.fullName}_${reportData.period}.pdf`);
            
            if (buttonToHide) buttonToHide.style.display = 'block';
        }).catch(err => { 
            toast.error("Error creating PDF.");
            if (buttonToHide) buttonToHide.style.display = 'block';
            Sentry.captureException(err); 
        });
    };
    
    const getStatusStyles = (status) => {
        switch (status) {
            case 'Present': case 'Completed': return 'bg-green-100 text-green-800';
            case 'Late': return 'bg-yellow-100 text-yellow-800';
            case 'Absent': return 'bg-red-100 text-red-800';
            case 'Holiday': return 'bg-purple-100 text-purple-800';
            case 'Scheduled': default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="container mx-auto">
            <div className="mb-8"><h1 className="admin-page-title">Employee Attendance Report</h1><p className="admin-page-sub">Generate and export monthly attendance reports.</p></div>
            
            <div className="bg-white p-6 rounded-xl shadow-md mb-8">
                <form onSubmit={handleGenerateReport} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                    <div className="md:col-span-3">
                        <label htmlFor="employee-select" className="block text-sm font-medium text-gray-600">Select Employee</label>
                        <select id="employee-select" className="mt-1 block w-full bg-gray-50 rounded-lg border p-3 font-semibold" required>
                             <option value="">-- Select --</option>
                             {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName} ({emp.employeeId})</option>)}
                        </select>
                    </div>
                     <div className="md:col-span-1">
                        <label htmlFor="month-select" className="block text-sm font-medium text-gray-600">Month</label>
                        <select id="month-select" defaultValue={new Date().getMonth()} className="mt-1 block w-full bg-gray-50 rounded-lg border p-3 font-semibold">
                            {Array.from({length: 12}, (_, i) => <option key={i} value={i}>{new Date(0,i).toLocaleString('en-US', {month: 'long'})}</option>)}
                        </select>
                    </div>
                     <div className="md:col-span-1">
                        <label htmlFor="year-select" className="block text-sm font-medium text-gray-600">Year</label>
                        <select id="year-select" defaultValue={new Date().getFullYear()} className="mt-1 w-full bg-gray-50 rounded-lg border p-3 font-semibold">
                             {Array.from({length: 5}, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
                        </select>
                    </div>
                    <button type="submit" disabled={isLoading} className="md:col-span-1 w-full text-white font-bold py-3 px-4 rounded-lg shadow-lg" style={{backgroundColor: '#c4151c'}}>
                        {isLoading ? "Generating..." : "Generate Report"}
                    </button>
                </form>
            </div>

            {reportData && (
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-8 border-b" ref={reportRef}>
                         <div className="flex justify-between items-center"> 
                            <div><h2 className="text-3xl font-bold text-gray-800">{reportData.employee.fullName}</h2><p className="text-gray-500">{reportData.period} Attendance Report</p></div>
                            <button onClick={handleExport} className="bg-green-600 text-white font-bold py-2 px-5 rounded-lg export-pdf-button">Export as PDF</button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-center">
                             <div className="bg-green-50 p-4 rounded-lg"><p className="text-sm font-semibold text-green-700">Present</p><p className="text-3xl font-bold text-green-800">{reportData.summary.present}</p></div>
                             <div className="bg-red-50 p-4 rounded-lg"><p className="text-sm font-semibold text-red-700">Absent</p><p className="text-3xl font-bold text-red-800">{reportData.summary.absent}</p></div>
                             <div className="bg-yellow-50 p-4 rounded-lg"><p className="text-sm font-semibold text-yellow-700">Late</p><p className="text-3xl font-bold text-yellow-800">{reportData.summary.late}</p></div>
                             <div className="bg-blue-50 p-4 rounded-lg"><p className="text-sm font-semibold text-blue-700">Holiday</p><p className="text-3xl font-extrabold">{reportData.summary.holiday}</p></div>
                        </div>
                         <div className="overflow-x-auto mt-6">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3">Date</th><th className="px-6 py-3">Status</th><th className="px-6 py-3">Check-in</th><th className="px-6 py-3">Check-out</th><th className="px-6 py-3">Location</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reportData.logs.map(log => (
                                        <tr key={log.date} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-900">{log.date}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusStyles(log.status)}`}>{log.status}</span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-600">{log.checkIn}</td>
                                            <td className="px-6 py-4 text-gray-600">{log.checkOut}</td>
                                            <td className="px-6 py-4 text-xs text-gray-600">
                                                <div className="relative group">
                                                    <span className="cursor-pointer">{log.location?.name || 'N/A'}</span>
                                                    {log.location?.lat && log.location?.lon && (
                                                        <div className="absolute bottom-full mb-2 hidden group-hover:block w-max bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
                                                            Lat: {log.location.lat.toFixed(4)}, Lon: {log.location.lon.toFixed(4)}
                                                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-800"></div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MonthlyReportSection;